import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { FileText, LogOut, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import CreditsDisplay from './CreditsDisplay';
import BuyCreditsModal from './BuyCreditsModal';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50 supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-900 hover:text-accent transition-colors"
              data-testid="nav-logo"
            >
              <div className="w-9 h-9 bg-accent rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-semibold text-lg tracking-tight hidden sm:block">
                Canva For Resume
              </span>
            </Link>

            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  <CreditsDisplay onClick={() => setShowBuyCredits(true)} />
                  
                  <Link to="/optimizer">
                    <Button 
                      variant="ghost" 
                      className="text-slate-600 hover:text-slate-900"
                      data-testid="nav-optimizer"
                    >
                      Optimize Resume
                    </Button>
                  </Link>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="flex items-center gap-2"
                        data-testid="nav-user-menu"
                      >
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-slate-600" />
                        </div>
                        <span className="hidden sm:block text-sm text-slate-600">
                          {user?.email?.split('@')[0]}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem 
                        onClick={() => setShowBuyCredits(true)}
                        className="cursor-pointer"
                      >
                        Buy Credits
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={handleLogout}
                        className="text-red-600 cursor-pointer"
                        data-testid="nav-logout"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" data-testid="nav-login">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button 
                      className="bg-accent text-white hover:bg-accent/90"
                      data-testid="nav-signup"
                    >
                      Get Started
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      
      <BuyCreditsModal 
        isOpen={showBuyCredits} 
        onClose={() => setShowBuyCredits(false)}
      />
    </>
  );
};

export default Navbar;